import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Building2, Users, CreditCard, TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { superAdminApi } from '../../utils/superAdminApi';
import { toast } from 'sonner';

export const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        superAdminApi.getStats(),
        superAdminApi.getTenants({})
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data.slice(0, 5)); // Last 5 tenants
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-emerald-500/20 text-emerald-400';
      case 'TRIAL': return 'bg-blue-500/20 text-blue-400';
      case 'SUSPENDED': return 'bg-red-500/20 text-red-400';
      case 'CANCELLED': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Attivo';
      case 'TRIAL': return 'Trial';
      case 'SUSPENDED': return 'Sospeso';
      case 'CANCELLED': return 'Cancellato';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Panoramica della piattaforma BollettinoX</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Totale Aziende</p>
                <p className="text-3xl font-bold text-white">{stats?.total_tenants || 0}</p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <Building2 className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Attive</p>
                <p className="text-3xl font-bold text-emerald-400">{stats?.active_tenants || 0}</p>
              </div>
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">In Trial</p>
                <p className="text-3xl font-bold text-blue-400">{stats?.trial_tenants || 0}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Sospese</p>
                <p className="text-3xl font-bold text-red-400">{stats?.suspended_tenants || 0}</p>
              </div>
              <div className="p-3 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants by Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-500" />
              Aziende per Piano
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.tenants_by_plan && Object.keys(stats.tenants_by_plan).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.tenants_by_plan).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <span className="text-slate-300">{plan}</span>
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 font-semibold">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Tenants */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              Aziende Recenti
            </CardTitle>
            <Link 
              to="/super-admin/tenants"
              className="text-sm text-amber-500 hover:text-amber-400"
            >
              Vedi tutte
            </Link>
          </CardHeader>
          <CardContent>
            {tenants.length > 0 ? (
              <div className="space-y-3">
                {tenants.map((tenant) => (
                  <Link
                    key={tenant.id}
                    to={`/super-admin/tenants/${tenant.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-white">{tenant.company_name}</p>
                      <p className="text-sm text-slate-400">{tenant.contact_email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(tenant.status)}`}>
                        {getStatusLabel(tenant.status)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Nessuna azienda registrata</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
